import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_record,
  fetch_pet_options,
  create_record,
  update_record,
  finalize_record,
  upload_prescription_doc,
  delete_prescription_doc,
} from './api';
import type { UpsertRecordInput } from './types';

const record_key = (booking_id: string) => ['vendor-record', booking_id] as const;

export function use_visit_record(booking_id: string, enabled = true) {
  return useQuery({
    queryKey: record_key(booking_id),
    queryFn: () => fetch_record(booking_id),
    enabled: enabled && booking_id.length > 0,
  });
}

/** Owner's pets for a petless visit. Only fetched when the booking has no pet. */
export function use_record_pet_options(booking_id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['vendor-record-pet-options', booking_id] as const,
    queryFn: () => fetch_pet_options(booking_id),
    enabled: enabled && booking_id.length > 0,
  });
}

export function use_save_record(booking_id: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: record_key(booking_id) });
    qc.invalidateQueries({ queryKey: ['vendor-bookings'] });
  };
  const create_m = useMutation({ mutationFn: (input: UpsertRecordInput) => create_record(booking_id, input), onSuccess: invalidate });
  const update_m = useMutation({ mutationFn: (input: UpsertRecordInput) => update_record(booking_id, input), onSuccess: invalidate });
  const finalize_m = useMutation({ mutationFn: () => finalize_record(booking_id), onSuccess: invalidate });
  const upload_doc_m = useMutation({ mutationFn: (file: File) => upload_prescription_doc(booking_id, file), onSuccess: invalidate });
  const delete_doc_m = useMutation({ mutationFn: (image_id: string) => delete_prescription_doc(booking_id, image_id), onSuccess: invalidate });
  return { create_m, update_m, finalize_m, upload_doc_m, delete_doc_m };
}
